import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  BookOpen, Search, LayoutDashboard, Building2, Users, User2, Briefcase,
  Clock, BarChart3, Shirt, PackageSearch, Settings, UserCog, Cog, Zap,
  FileText, Calendar, DollarSign, MapPin, CheckSquare, Truck, ShieldCheck,
  ClipboardList, Boxes, ArrowRightLeft, ChevronRight, Printer, Eye,
  LogIn, KeyRound, Bell, Moon, Sun
} from 'lucide-react';

// ─── Definición de secciones del manual ────────────────────────────────────────

const secciones = [
  {
    id: 'inicio-sesion',
    titulo: 'Inicio de Sesión',
    icono: LogIn,
    color: 'text-blue-500',
    resumen: 'Cómo acceder al sistema y gestionar tu cuenta.',
    contenido: [
      {
        subtitulo: 'Iniciar Sesión',
        pasos: [
          'Ingresa a la URL del aplicativo desde tu navegador.',
          'Escribe tu correo electrónico y contraseña en el formulario de inicio de sesión.',
          'Haz clic en el botón "Iniciar Sesión".',
          'Si tus credenciales son correctas, serás redirigido al módulo principal según tu rol.',
        ],
      },
      {
        subtitulo: 'Recuperar Contraseña',
        pasos: [
          'Contacte al equipo de soporte tecnico lo guiara para la recuperación de contraseña',
        ],
      }
    ],
  },
  {
    id: 'navegacion',
    titulo: 'Navegación General',
    icono: Eye,
    color: 'text-indigo-500',
    resumen: 'Cómo moverte dentro del aplicativo.',
    contenido: [
      {
        subtitulo: 'Barra de Navegación',
        pasos: [
          'La barra superior muestra los módulos a los que tienes acceso según tu rol.',
          'Algunos módulos tienen submenús desplegables; haz clic en el nombre para ver las opciones.',
          'En dispositivos móviles, usa el ícono de menú (☰) para abrir el menú lateral.',
        ],
      },
      {
        subtitulo: 'Tema Claro / Oscuro',
        pasos: [
          'Haz clic en tu avatar en la esquina superior derecha.',
          'Encontrarás los botones de tema (claro / oscuro / sistema) para cambiar la apariencia.',
        ],
      },
      {
        subtitulo: 'Versión del Aplicativo',
        pasos: [
          'En la barra de navegación aparece un badge con la versión actual (ej. v1.2.3).',
          'Haz clic en él para ver las novedades y cambios de la versión.',
        ],
      },
    ],
  },
  {
    id: 'dashboard',
    titulo: 'Dashboard / Reportes',
    icono: LayoutDashboard,
    color: 'text-emerald-500',
    resumen: 'Panel principal con indicadores y reportes generales.',
    contenido: [
      {
        subtitulo: 'Vista General',
        pasos: [
          'El Dashboard muestra un resumen consolidado de la operación.',
          'Puedes ver indicadores clave como producción, entregas, recolecciones, etc.',
          'Usa los filtros de fecha o centro de lavado para segmentar la información.',
        ],
      },
      {
        subtitulo: 'Exportar Reportes',
        pasos: [
          'Algunos reportes permiten exportar a Excel o PDF.',
          'Busca el botón de descarga/exportar en la sección correspondiente.',
        ],
      },
    ],
  },
  {
    id: 'centros-lavado',
    titulo: 'Centros de Lavado',
    icono: Building2,
    color: 'text-orange-500',
    resumen: 'Gestión de centros de lavado y sus operaciones.',
    contenido: [
      {
        subtitulo: 'Ver Centros de Lavado',
        pasos: [
          'Navega a "Centros" en el menú principal.',
          'Verás las tarjetas de cada centro de lavado registrado.',
          'Haz clic en una tarjeta para acceder al dashboard del centro.',
        ],
      },
      {
        subtitulo: 'Dashboard del Centro',
        pasos: [
          'Dentro de cada centro encontrarás accesos rápidos a: Recepción, Ciclos de Lavado, Alistamientos, Rutas, Inventario de Clientes, Servicios Públicos, Productos, Horas Trabajadas, Turnos y Documentos.',
          'Cada tarjeta te lleva al módulo correspondiente dentro del centro seleccionado.',
        ],
      },
    ],
  },
  {
    id: 'recepcion',
    titulo: 'Recepción de Ropa Sucia',
    icono: ClipboardList,
    color: 'text-red-500',
    resumen: 'Registro de ropa sucia recibida de los clientes.',
    contenido: [
      {
        subtitulo: 'Crear Recepción',
        pasos: [
          'Desde el dashboard del centro, ingresa a "Recepción".',
          'Selecciona el cliente de la lista.',
          'Agrega las prendas recibidas indicando tipo, cantidad y peso.',
          'Revisa el resumen y confirma la recepción.',
          'Se generará un consecutivo automático (REC-XXXXX).',
        ],
      },
      {
        subtitulo: 'Verificación de Ropa Sucia',
        pasos: [
          'Accede a "Verificación de Ropa" desde el dashboard del centro.',
          'Ingresa el consecutivo de la recolección.',
          'Verifica las cantidades recibidas (puedes ajustar si hay diferencias).',
          'Confirma la verificación. Esto actualiza el inventario del cliente.',
        ],
      },
    ],
  },
  {
    id: 'ciclos-lavado',
    titulo: 'Ciclos de Lavado',
    icono: Cog,
    color: 'text-cyan-500',
    resumen: 'Registro y seguimiento de ciclos en las lavadoras.',
    contenido: [
      {
        subtitulo: 'Registrar un Ciclo',
        pasos: [
          'Desde el dashboard del centro, ingresa a "Ciclos de Lavado".',
          'Selecciona la lavadora disponible.',
          'Elige el programa de lavado y registra los datos del ciclo (peso, prendas, etc.).',
          'Inicia el ciclo. El sistema registrará hora de inicio.',
          'Al finalizar, marca el ciclo como completado.',
        ],
      },
    ],
  },
  {
    id: 'alistamientos',
    titulo: 'Alistamientos',
    icono: Boxes,
    color: 'text-violet-500',
    resumen: 'Preparación de ropa limpia para entrega a clientes.',
    contenido: [
      {
        subtitulo: 'Crear Alistamiento',
        pasos: [
          'Desde el dashboard del centro, ingresa a "Alistamientos".',
          'Selecciona el cliente destino.',
          'Agrega las prendas limpias que se van a alistar (tipo, cantidad).',
          'Confirma el alistamiento.',
        ],
      },
      {
        subtitulo: 'Marcar como Entregado',
        pasos: [
          'Cuando el alistamiento esté listo para despacho, márcalo como "Entregado".',
          'Esto creará automáticamente un registro de entrega en el módulo de Rutas.',
        ],
      },
    ],
  },
  {
    id: 'rutas',
    titulo: 'Rutas / Entregas y Recolecciones',
    icono: Truck,
    color: 'text-amber-500',
    resumen: 'Gestión de rutas de entrega de ropa limpia y recolección de ropa sucia.',
    contenido: [
      {
        subtitulo: 'Entregas Alistadas',
        pasos: [
          'En la pestaña "Alistadas" verás las entregas pendientes de envío.',
          'Selecciona las entregas que deseas poner en ruta.',
          'Asigna un conductor y confirma. Pasarán a estado "En Camino".',
        ],
      },
      {
        subtitulo: 'Entregas En Camino',
        pasos: [
          'La pestaña "En Camino" muestra entregas en tránsito.',
          'El conductor puede confirmar la entrega al cliente (ajustando cantidades si es necesario).',
          'Al confirmar, la entrega pasa a estado "Entregado".',
        ],
      },
      {
        subtitulo: 'Recolección de Ropa Sucia',
        pasos: [
          'En la pestaña "Recolección", busca al cliente.',
          'Selecciona las prendas sucias a recoger con sus cantidades.',
          'Confirma la recolección. Se generará un consecutivo REC-XXXXX.',
          'La recolección queda registrada y puede imprimirse un ticket.',
        ],
      },
    ],
  },
  {
    id: 'inventario-clientes',
    titulo: 'Inventario de Clientes',
    icono: ArrowRightLeft,
    color: 'text-teal-500',
    resumen: 'Control de prendas en poder de cada cliente.',
    contenido: [
      {
        subtitulo: 'Ver Inventario',
        pasos: [
          'Desde el dashboard del centro, accede a "Inventario Clientes".',
          'Verás una lista de clientes con el total de prendas en inventario.',
          'Haz clic en un cliente para ver el detalle.',
        ],
      },
      {
        subtitulo: 'Detalle del Cliente',
        pasos: [
          'Dentro del detalle encontrarás pestañas: Inventario, Movimientos, Recolecciones, Entregas y Alistamientos.',
          'El inventario muestra el saldo actual de prendas.',
          'Movimientos muestra el historial de entradas y salidas.',
        ],
      },
    ],
  },
  {
    id: 'conductor',
    titulo: 'Portal del Conductor',
    icono: Truck,
    color: 'text-lime-500',
    resumen: 'Panel exclusivo para conductores de rutas.',
    contenido: [
      {
        subtitulo: 'Uso del Portal',
        pasos: [
          'El conductor accede directamente al módulo "Conductor" al iniciar sesión.',
          'Desde aquí puede ver sus entregas asignadas y rutas del día.',
          'Puede confirmar entregas y registrar recolecciones en campo.',
        ],
      },
    ],
  },
  {
    id: 'facturacion',
    titulo: 'Facturación y Liquidación',
    icono: DollarSign,
    color: 'text-green-500',
    resumen: 'Generación de liquidaciones y facturación a clientes.',
    contenido: [
      {
        subtitulo: 'Liquidación',
        pasos: [
          'Navega a "Facturación > Liquidación" en el menú principal.',
          'Selecciona el período y el cliente.',
          'El sistema calculará automáticamente los valores con base en los servicios prestados.',
          'Revisa el detalle y genera la liquidación.',
        ],
      },
      {
        subtitulo: 'Facturación',
        pasos: [
          'En "Facturación > Facturación" puedes ver las facturas generadas.',
          'Puedes filtrar por fecha, cliente o estado.',
          'Las facturas pueden exportarse o imprimirse.',
        ],
      },
    ],
  },
  {
    id: 'operario',
    titulo: 'Portal del Operario',
    icono: Briefcase,
    color: 'text-pink-500',
    resumen: 'Panel para operarios con registro de llegada, turnos y documentos.',
    contenido: [
      {
        subtitulo: 'Registro de Llegada',
        pasos: [
          'Al ingresar, el operario ve su Dashboard con accesos rápidos.',
          'Haz clic en "Registrar Llegada" para marcar tu hora de entrada.',
          'El sistema registra automáticamente la fecha y hora.',
        ],
      },
      {
        subtitulo: 'Mis Registros',
        pasos: [
          'En "Registros" puedes ver tu historial de llegadas y salidas.',
        ],
      },
      {
        subtitulo: 'Mis Turnos',
        pasos: [
          'En "Turnos" puedes consultar los turnos asignados.',
        ],
      },
      {
        subtitulo: 'Mis Documentos',
        pasos: [
          'En "Documentos" puedes ver y subir documentos personales requeridos.',
        ],
      },
    ],
  },
  {
    id: 'portal-clientes',
    titulo: 'Portal de Clientes',
    icono: User2,
    color: 'text-sky-500',
    resumen: 'Portal para que los clientes vean sus entregas y recepciones.',
    contenido: [
      {
        subtitulo: 'Dashboard del Cliente',
        pasos: [
          'Al iniciar sesión como cliente, verás tu panel personalizado.',
          'Puedes ver el estado de tus entregas y recolecciones.',
        ],
      },
      {
        subtitulo: 'Recepción de Ropa',
        pasos: [
          'En "Recepción" puedes ver el detalle de la ropa recibida.',
          'Puedes confirmar la recepción y reportar diferencias.',
        ],
      },
    ],
  },
  {
    id: 'control-empleados',
    titulo: 'Control de Personal',
    icono: Calendar,
    color: 'text-rose-500',
    resumen: 'Seguimiento de asistencia y control del personal.',
    contenido: [
      {
        subtitulo: 'Ver Control de Empleados',
        pasos: [
          'Navega a "Administrativo > Control personal".',
          'Verás un calendario/tabla con la asistencia de los empleados.',
          'Puedes filtrar por centro de lavado, empleado o rango de fechas.',
        ],
      },
    ],
  },
  {
    id: 'nomina',
    titulo: 'Nómina / Liquidación de Personal',
    icono: DollarSign,
    color: 'text-emerald-600',
    resumen: 'Cálculo y liquidación de nómina del personal.',
    contenido: [
      {
        subtitulo: 'Generar Liquidación',
        pasos: [
          'Navega a "Administrativo > Nómina".',
          'Selecciona el período de liquidación.',
          'Revisa los cálculos automáticos (horas trabajadas, extras, deducciones).',
          'Confirma y genera la liquidación.',
        ],
      },
    ],
  },
  {
    id: 'parametrizacion',
    titulo: 'Parametrización',
    icono: Settings,
    color: 'text-gray-500',
    resumen: 'Configuración de datos maestros del sistema.',
    contenido: [
      {
        subtitulo: 'Empleados',
        pasos: [
          'En "Parametrización > Empleados" puedes crear, editar y desactivar empleados.',
          'Cada empleado tiene: nombre, documento, cargo, centro asignado, datos de contacto.',
        ],
      },
      {
        subtitulo: 'Clientes',
        pasos: [
          'En "Parametrización > Clientes" gestiona los clientes del servicio.',
          'Puedes asignar tipos de prenda, precios y configuraciones específicas.',
        ],
      },
      {
        subtitulo: 'Productos',
        pasos: [
          'En "Parametrización > Productos" administra los insumos y productos utilizados.',
        ],
      },
      {
        subtitulo: 'Centros de Lavado',
        pasos: [
          'En "Parametrización > Centros de Lavado" configura los centros operativos.',
        ],
      },
      {
        subtitulo: 'Lavadoras',
        pasos: [
          'En "Parametrización > Lavadoras" registra las máquinas de cada centro.',
          'Configura capacidad, programa por defecto, y estado (activa/inactiva).',
        ],
      },
      {
        subtitulo: 'Tipos de Prenda',
        pasos: [
          'Define los tipos de prenda que maneja la lavandería (sábanas, toallas, uniformes, etc.).',
        ],
      },
      {
        subtitulo: 'Programas de Lavado',
        pasos: [
          'Configura los programas de lavado disponibles (temperatura, duración, tipo de lavado).',
        ],
      },
      {
        subtitulo: 'Servicios',
        pasos: [
          'Administra los servicios adicionales (planchado, desmanche, etc.).',
        ],
      },
      {
        subtitulo: 'Proveedores',
        pasos: [
          'Gestiona los proveedores de insumos y servicios.',
        ],
      },
      {
        subtitulo: 'Usuarios',
        pasos: [
          'En "Parametrización > Usuarios" crea y administra los usuarios del sistema.',
          'Asigna roles (Admin, Supervisor, Operario, Cliente, etc.).',
          'Puedes activar/desactivar módulos específicos por usuario.',
        ],
      },
    ],
  },
  {
    id: 'tareas',
    titulo: 'Tareas / Solicitudes',
    icono: CheckSquare,
    color: 'text-purple-500',
    resumen: 'Gestión de tareas y solicitudes de desarrollo.',
    contenido: [
      {
        subtitulo: 'Crear y Gestionar Tareas',
        pasos: [
          'Navega a "Administrativo > Tareas".',
          'Puedes crear nuevas solicitudes describiendo la necesidad.',
          'Asigna prioridad y seguimiento a cada tarea.',
          'Las tareas tienen estados: Pendiente, En Progreso, Completada.',
        ],
      },
    ],
  },
  {
    id: 'documentacion',
    titulo: 'Documentación',
    icono: FileText,
    color: 'text-yellow-600',
    resumen: 'Gestión documental de empleados y centros de lavado.',
    contenido: [
      {
        subtitulo: 'Documentos de Empleados',
        pasos: [
          'Navega a "Documentación > Empleados".',
          'Selecciona un empleado para ver o subir sus documentos.',
          'Puedes cargar archivos (PDF, imágenes) y asignar tipo de documento.',
        ],
      },
      {
        subtitulo: 'Documentos de Centros',
        pasos: [
          'En "Documentación > Centros" gestiona documentos legales y operativos de cada centro.',
        ],
      },
    ],
  },
];

// ─── Componente principal ──────────────────────────────────────────────────────

const ManualPage = () => {
  const [busqueda, setBusqueda] = useState('');
  const [seccionActiva, setSeccionActiva] = useState(secciones[0].id);

  const seccionesFiltradas = secciones.filter((s) => {
    if (!busqueda.trim()) return true;
    const q = busqueda.toLowerCase();
    return (
      s.titulo.toLowerCase().includes(q) ||
      s.resumen.toLowerCase().includes(q) ||
      s.contenido.some(
        (c) =>
          c.subtitulo.toLowerCase().includes(q) ||
          c.pasos.some((p) => p.toLowerCase().includes(q))
      )
    );
  });

  const seccionSeleccionada =
    seccionesFiltradas.find((s) => s.id === seccionActiva) || seccionesFiltradas[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <BookOpen className="h-7 w-7 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Manual de Procedimientos</h1>
            <p className="text-muted-foreground text-sm">
              Instructivo de uso del aplicativo para usuarios
            </p>
          </div>
        </div>

        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar en el manual..."
            value={busqueda}
            onChange={(e) => {
              setBusqueda(e.target.value);
              // Si la sección activa ya no está en los filtrados, seleccionar la primera
              const filtered = secciones.filter((s) => {
                const q = e.target.value.toLowerCase();
                if (!q) return true;
                return (
                  s.titulo.toLowerCase().includes(q) ||
                  s.resumen.toLowerCase().includes(q) ||
                  s.contenido.some(
                    (c) =>
                      c.subtitulo.toLowerCase().includes(q) ||
                      c.pasos.some((p) => p.toLowerCase().includes(q))
                  )
                );
              });
              if (filtered.length && !filtered.find((s) => s.id === seccionActiva)) {
                setSeccionActiva(filtered[0].id);
              }
            }}
            className="pl-9"
          />
        </div>
      </motion.div>

      {/* Contenido: sidebar + detalle */}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Sidebar - Índice */}
        <Card className="lg:sticky lg:top-24 lg:self-start">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Índice</CardTitle>
            <CardDescription>{seccionesFiltradas.length} secciones</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[calc(100vh-320px)]">
              <div className="px-4 pb-4 space-y-1">
                {seccionesFiltradas.map((seccion) => {
                  const Icon = seccion.icono;
                  const isActive = seccionSeleccionada?.id === seccion.id;
                  return (
                    <button
                      key={seccion.id}
                      onClick={() => setSeccionActiva(seccion.id)}
                      className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors text-sm ${
                        isActive
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'hover:bg-muted text-foreground/80'
                      }`}
                    >
                      <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary' : seccion.color}`} />
                      <span className="truncate">{seccion.titulo}</span>
                      {isActive && <ChevronRight className="h-4 w-4 ml-auto flex-shrink-0" />}
                    </button>
                  );
                })}
                {seccionesFiltradas.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No se encontraron resultados para "{busqueda}"
                  </p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Detalle de la sección */}
        {seccionSeleccionada ? (
          <motion.div
            key={seccionSeleccionada.id}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25 }}
          >
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-primary/10`}>
                    <seccionSeleccionada.icono className={`h-6 w-6 ${seccionSeleccionada.color}`} />
                  </div>
                  <div>
                    <CardTitle className="text-xl">{seccionSeleccionada.titulo}</CardTitle>
                    <CardDescription>{seccionSeleccionada.resumen}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-8">
                {seccionSeleccionada.contenido.map((bloque, idx) => (
                  <div key={idx} className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs font-normal">
                        {idx + 1}
                      </Badge>
                      <h3 className="font-semibold text-base">{bloque.subtitulo}</h3>
                    </div>
                    <ol className="space-y-2 ml-2">
                      {bloque.pasos.map((paso, pIdx) => (
                        <li key={pIdx} className="flex items-start gap-3 text-sm">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium mt-0.5">
                            {pIdx + 1}
                          </span>
                          <span className="text-foreground/90 leading-relaxed">{paso}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        ) : null}
      </div>
    </div>
  );
};

export default ManualPage;
